import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import "../AdminDashboard/AdminDashboard.css";
import "./GymAssets.css";
import {
  JOB_TYPES,
  conditionLabel,
  formatDate,
  hoursLabel,
  jobTypeLabel,
  money,
  statusLabel,
} from "./gymAssetOptions";

const emptyJob = () => ({
  job_type: "repair",
  title: "",
  details: "",
  cost: "",
  vendor: "",
  started_at: new Date().toISOString().slice(0, 16),
  ended_at: "",
  status: "open",
});

export default function GymAssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [branches, setBranches] = useState([]);
  const [job, setJob] = useState(emptyJob());
  const [move, setMove] = useState({ branch_id: "", location: "", note: "" });
  const [showJob, setShowJob] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await UserServices.gymAsset(id);
    setAsset(res.data?.data || null);
  };

  useEffect(() => {
    UserServices.branchNameAll().then((res) => setBranches(res.data?.data || [])).catch(() => setBranches([]));
    load().catch(() => toast.error("Could not open this asset"));
  }, [id]);

  const branchName = (branchId) =>
    branches.find((branch) => String(branch.id) === String(branchId))?.name || "—";

  const addJob = async (event) => {
    event.preventDefault();
    if (!job.title.trim()) {
      toast.error("Give the repair a short title");
      return;
    }
    setSaving(true);
    try {
      await UserServices.addGymAssetJob(id, {
        ...job,
        cost: job.cost === "" ? 0 : Number(job.cost),
        started_at: job.started_at.replace("T", " "),
        ended_at: job.ended_at ? job.ended_at.replace("T", " ") : null,
      });
      toast.success("Repair saved");
      setJob(emptyJob());
      setShowJob(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save the repair");
    } finally {
      setSaving(false);
    }
  };

  const closeJob = async (row) => {
    try {
      await UserServices.updateGymAssetJob(row.id, {
        ...row,
        status: "done",
        ended_at: new Date().toISOString().slice(0, 19).replace("T", " "),
      });
      toast.success("Repair closed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not close the repair");
    }
  };

  const transfer = async (event) => {
    event.preventDefault();
    if (!move.branch_id) {
      toast.error("Choose the branch this asset is moving to");
      return;
    }
    setSaving(true);
    try {
      await UserServices.transferGymAsset(id, move);
      toast.success("Asset moved");
      setMove({ branch_id: "", location: "", note: "" });
      setShowMove(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not move the asset");
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async () => {
    try {
      const res = await UserServices.duplicateGymAsset(id);
      toast.success("Copy created with a new asset code");
      navigate("/gym-assets/" + res.data.data.id);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not copy the asset");
    }
  };

  if (!asset) {
    return (
      <div className="wrapper gym-assets">
        <main className="page-content"><div className="site-main-design ad-shell p-4">Loading asset...</div></main>
      </div>
    );
  }

  const dayStamp = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  };
  const today = dayStamp(new Date());
  const soon = dayStamp(new Date(Date.now() + 30 * 86400000));
  const warrantySoon = asset.warranty_end && asset.warranty_end >= today && asset.warranty_end <= soon;

  return (
    <div className="wrapper gym-assets">
      <ToastContainer />
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="ad-topbar d-flex justify-content-between align-items-start gap-3">
              <div>
                <p className="ad-eyebrow"><Link to="/gym-assets">Asset Management</Link></p>
                <h2 className="ad-title">{asset.name}</h2>
                <p className="ad-subtitle">
                  {asset.asset_code} · {asset.branch_name} · {asset.category}
                  {asset.location ? ` · ${asset.location}` : ""}
                </p>
              </div>
              <div className="gym-actions">
                <span className={`gym-pill is-${asset.status}`}>{statusLabel(asset.status)}</span>
                <Link className="btn btn-outline-secondary" to={`/gym-maintenance?asset=${asset.id}`}>Maintenance</Link>
                <button type="button" className="btn btn-outline-secondary" onClick={duplicate}>Copy</button>
                <Link className="btn btn-danger" to={`/gym-assets/${asset.id}/edit`}>Edit</Link>
              </div>
            </div>

            <div className="gym-kpis mb-3">
              <div className="gym-kpi"><span>Purchase cost</span><strong>{money(asset.total_cost)}</strong></div>
              <div className="gym-kpi"><span>Book value</span><strong>{money(asset.book_value)}</strong></div>
              <div className="gym-kpi"><span>Repair cost</span><strong>{money(asset.maintenance_cost)}</strong></div>
              <div className="gym-kpi"><span>Downtime</span><strong>{hoursLabel(asset.downtime_minutes)}</strong></div>
              <div className="gym-kpi"><span>Open jobs</span><strong>{(asset.jobs || []).filter((row) => row.status === "open").length}</strong></div>
              <div className="gym-kpi"><span>Condition</span><strong>{conditionLabel(asset.condition)}</strong></div>
            </div>

            <div className="gym-detail">
              <div>
                <section className="gym-panel p-3 mb-3">
                  <div className="gym-panel-head"><h3>Where it is</h3></div>
                  <div className="gym-facts">
                    <Fact label="Branch" value={asset.branch_name} />
                    <Fact label="Location" value={asset.location} />
                    <Fact label="Department" value={asset.department} />
                    <Fact label="Custodian" value={asset.custodian} />
                  </div>
                </section>
                <section className="gym-panel p-3 mb-3">
                  <div className="gym-panel-head"><h3>Purchase</h3></div>
                  <div className="gym-facts">
                    <Fact label="Quantity" value={asset.quantity} />
                    <Fact label="Unit price" value={asset.unit_price == null ? "—" : money(asset.unit_price)} />
                    <Fact label="Purchased" value={formatDate(asset.purchase_date)} />
                    <Fact label="Supplier" value={asset.supplier} />
                    <Fact label="Invoice" value={asset.invoice_number} />
                    <Fact label="Payment" value={asset.payment_method} />
                  </div>
                </section>
                <section className="gym-panel p-3">
                  <div className="gym-panel-head"><h3>Identity and warranty</h3></div>
                  <div className="gym-facts">
                    <Fact label="Brand" value={asset.brand} />
                    <Fact label="Model" value={asset.model} />
                    <Fact label="Serial" value={asset.serial_number} />
                    <Fact label="Subcategory" value={asset.subcategory} />
                    <Fact label="Warranty ends" value={formatDate(asset.warranty_end)} />
                    <Fact label="Useful life" value={asset.useful_life_years ? `${asset.useful_life_years} years` : "—"} />
                  </div>
                  {warrantySoon ? <p className="gym-note">Warranty ends within 30 days.</p> : null}
                  {asset.remarks ? <p className="mb-0 mt-3">{asset.remarks}</p> : null}
                </section>
              </div>

              <div>
                <section className="gym-panel p-3 mb-3">
                  <div className="gym-panel-head">
                    <div>
                      <h3>Repair or downtime</h3>
                      <p>An open job marks the asset under repair.</p>
                    </div>
                  </div>
                  <button type="button" className="btn btn-danger" onClick={() => setShowJob((open) => !open)}>
                    {showJob ? "Hide form" : "Record a job"}
                  </button>
                  {showJob ? (
                    <form className="gym-form-grid mt-3" onSubmit={addJob}>
                      <label className="gym-field">Type
                        <select className="form-select" value={job.job_type} onChange={(e) => setJob({ ...job, job_type: e.target.value })}>
                          {JOB_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                      </label>
                      <label className="gym-field">Title
                        <input className="form-control" value={job.title} onChange={(e) => setJob({ ...job, title: e.target.value })} />
                      </label>
                      <label className="gym-field">Cost (PKR)
                        <input className="form-control" type="number" min="0" value={job.cost} onChange={(e) => setJob({ ...job, cost: e.target.value })} />
                      </label>
                      <label className="gym-field">Vendor
                        <input className="form-control" value={job.vendor} onChange={(e) => setJob({ ...job, vendor: e.target.value })} />
                      </label>
                      <label className="gym-field">Started
                        <input className="form-control" type="datetime-local" value={job.started_at} onChange={(e) => setJob({ ...job, started_at: e.target.value })} />
                      </label>
                      <label className="gym-field">Ended
                        <input className="form-control" type="datetime-local" value={job.ended_at} onChange={(e) => setJob({ ...job, ended_at: e.target.value, status: e.target.value ? "done" : "open" })} />
                      </label>
                      <label className="gym-field gym-span-2">What was done
                        <textarea className="form-control" rows="2" value={job.details} onChange={(e) => setJob({ ...job, details: e.target.value })} />
                      </label>
                      <div><button className="btn btn-danger" disabled={saving} type="submit">Save job</button></div>
                    </form>
                  ) : null}
                </section>

                <section className="gym-panel p-3">
                  <div className="gym-panel-head">
                    <div>
                      <h3>Move branch</h3>
                      <p>Now at {asset.branch_name}{asset.location ? `, ${asset.location}` : ""}.</p>
                    </div>
                  </div>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowMove((open) => !open)}>
                    {showMove ? "Hide form" : "Move this asset"}
                  </button>
                  {showMove ? (
                    <form className="gym-form-grid mt-3" onSubmit={transfer}>
                      <label className="gym-field">Branch
                        <select className="form-select" value={move.branch_id} onChange={(e) => setMove({ ...move, branch_id: e.target.value })}>
                          <option value="">Select branch</option>
                          {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                        </select>
                      </label>
                      <label className="gym-field">New location
                        <input className="form-control" value={move.location} onChange={(e) => setMove({ ...move, location: e.target.value })} />
                      </label>
                      <label className="gym-field gym-span-2">Note
                        <input className="form-control" value={move.note} onChange={(e) => setMove({ ...move, note: e.target.value })} />
                      </label>
                      <div><button className="btn btn-outline-secondary" disabled={saving} type="submit">Move asset</button></div>
                    </form>
                  ) : null}
                </section>
              </div>
            </div>

            <section className="gym-panel p-3 mt-3">
              <div className="gym-panel-head">
                <h3>Job history</h3>
                <span className="gym-count">{(asset.jobs || []).length} jobs</span>
              </div>
              <div className="table-responsive">
                <table className="table gym-table mb-0">
                  <thead><tr><th>Job</th><th>When</th><th>Cost</th><th>Downtime</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {(asset.jobs || []).length === 0 ? <tr><td colSpan="6">No repairs yet.</td></tr> : asset.jobs.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="gym-asset-name">{row.title}</div>
                          <div className="gym-asset-meta">{jobTypeLabel(row.job_type)}{row.vendor ? ` · ${row.vendor}` : ""}</div>
                        </td>
                        <td>{formatDate(row.started_at)}{row.ended_at ? ` – ${formatDate(row.ended_at)}` : ""}</td>
                        <td>{money(row.cost)}</td>
                        <td>{hoursLabel(row.downtime_minutes)}</td>
                        <td><span className={`gym-pill ${row.status === "open" ? "is-under_repair" : "is-active"}`}>{row.status === "open" ? "Open" : "Done"}</span></td>
                        <td>{row.status === "open" ? <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => closeJob(row)}>Close</button> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="gym-panel p-3 mt-3">
              <div className="gym-panel-head">
                <h3>Transfer history</h3>
                <span className="gym-count">{(asset.moves || []).length} moves</span>
              </div>
              <div className="table-responsive">
                <table className="table gym-table mb-0">
                  <thead><tr><th>From</th><th>To</th><th>Note</th><th>When</th></tr></thead>
                  <tbody>
                    {(asset.moves || []).length === 0 ? <tr><td colSpan="4">This asset has not been moved.</td></tr> : asset.moves.map((row) => (
                      <tr key={row.id}>
                        <td>{branchName(row.from_branch_id)}{row.from_location ? ` · ${row.from_location}` : ""}</td>
                        <td>{branchName(row.to_branch_id)}{row.to_location ? ` · ${row.to_location}` : ""}</td>
                        <td>{row.note || "—"}</td>
                        <td>{formatDate(row.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="gym-fact">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}
